import { useCallback, useState } from 'react'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Photo } from '@/types/visit'

function formatTag(tag: string): string {
  return tag
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

interface PhotoViewerProps {
  photos: Photo[]
  title?: string
}

export default function PhotoViewer({ photos, title = 'Photos' }: PhotoViewerProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [isZoomed, setIsZoomed] = useState(false)

  const sorted = [...photos].sort((a, b) => a.order - b.order)

  const goToPrev = useCallback(() => {
    if (selectedIndex === null) return
    setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : sorted.length - 1)
  }, [selectedIndex, sorted.length])

  const goToNext = useCallback(() => {
    if (selectedIndex === null) return
    setSelectedIndex(selectedIndex < sorted.length - 1 ? selectedIndex + 1 : 0)
  }, [selectedIndex, sorted.length])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      goToPrev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      goToNext()
    }
  }, [goToPrev, goToNext])

  if (sorted.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-6">
        <h2 className="text-lg font-medium text-slate-900">{title}</h2>
        <p className="mt-4 text-center text-sm text-slate-500">No photos uploaded.</p>
      </div>
    )
  }

  const selected = selectedIndex !== null ? sorted[selectedIndex] : null

  return (
    <>
      <div className="rounded-lg border bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">{title}</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {sorted.map((photo, index) => (
            <button
              key={photo.id}
              onClick={() => { setSelectedIndex(index); setIsZoomed(false) }}
              className="group relative aspect-square overflow-hidden rounded-lg border bg-slate-100 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            >
              <img
                src={photo.thumbnail_url}
                alt={`${formatTag(photo.tag)} photo`}
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
              <Badge
                variant="secondary"
                className="absolute bottom-1.5 left-1.5 text-xs"
              >
                {formatTag(photo.tag)}
              </Badge>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent className="max-w-3xl p-0" onKeyDown={handleKeyDown}>
          <DialogTitle className="sr-only">
            {selected ? `${formatTag(selected.tag)} photo` : 'Photo viewer'}
          </DialogTitle>
          {selected && selectedIndex !== null && (
            <div className="flex flex-col">
              <div className="relative flex items-center justify-center overflow-hidden bg-black" style={{ minHeight: '60vh' }}>
                <img
                  src={selected.url}
                  alt={`${formatTag(selected.tag)} photo`}
                  className={`max-h-[75vh] transition-transform ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
                  onClick={() => setIsZoomed(!isZoomed)}
                  style={isZoomed ? { transform: 'scale(2)' } : undefined}
                />
                {sorted.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Previous photo"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                      onClick={goToPrev}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Next photo"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70"
                      onClick={goToNext}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between border-t bg-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <Badge>{formatTag(selected.tag)}</Badge>
                  <span className="text-xs text-slate-500">{selected.filename}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
                  onClick={() => setIsZoomed(!isZoomed)}
                >
                  {isZoomed ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
